// components/vcard/shared/OwnerSection.tsx
import React, { useEffect, useState } from "react";
import type {
  VCard,
  MessagingApp,
  MessagingContact,
} from "../../../types/vcard";
import { DEFAULT_COUNTRY_CODE } from "../../../constants/countryCodes";
import {
  formatPhoneBodyForDisplay,
  normalizePhoneForStorage,
  stripCountryCodeForDisplay,
} from "../../../utils/phone";
import CountryCodeSelect from "../../ui/CountryCodeSelect";

const MESSAGING_APPS: MessagingApp[] = [
  "WhatsApp",
  "iMessage",
  "Messenger",
  "Telegram",
  "Snapchat",
  "WeChat",
  "Line",
];

const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

interface OwnerSectionProps {
  category?: string;
  formData: OwnerFormData;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  mobilePhoneCountryCode?: string;
  setMobilePhoneCountryCode?: (code: string) => void;
  workPhoneCountryCode?: string;
  setWorkPhoneCountryCode?: (code: string) => void;
  travelPhoneCountryCode?: string;
  setTravelPhoneCountryCode?: (code: string) => void;
  hideTitle?: boolean;
}

type OwnerFormData = Partial<VCard> & {
  mobilePhoneCountryCode?: string;
  workPhoneCountryCode?: string;
  travelPhoneCountryCode?: string;
};

type MessagingHandleMap = Partial<Record<MessagingApp, string>>;

const normalizeMessagingContacts = (
  handlesByApp: MessagingHandleMap,
): MessagingContact[] =>
  MESSAGING_APPS.map((app) => ({
    app,
    handle: (handlesByApp[app] || "").trim(),
  })).filter((contact) => Boolean(contact.handle));

const getMessagingState = (
  formData: Pick<OwnerFormData, "messagingContact" | "messagingContacts">,
): { selectedApp: MessagingApp | ""; handlesByApp: MessagingHandleMap } => {
  const sourceContacts = Array.isArray(formData.messagingContacts)
    ? formData.messagingContacts
    : formData.messagingContact
      ? [formData.messagingContact]
      : [];

  const handlesByApp: MessagingHandleMap = {};
  sourceContacts.forEach((contact) => {
    if (contact?.app) {
      handlesByApp[contact.app] = contact.handle ?? "";
    }
  });

  const selectedApp =
    formData.messagingContact?.app &&
    MESSAGING_APPS.includes(formData.messagingContact.app)
      ? formData.messagingContact.app
      : sourceContacts[0]?.app || "";

  return {
    selectedApp,
    handlesByApp,
  };
};

const getSelectedHandle = (
  selectedApp: MessagingApp | "",
  handlesByApp: MessagingHandleMap,
): string => {
  if (!selectedApp) {
    return "";
  }

  return handlesByApp[selectedApp] || "";
};

const OwnerSection: React.FC<OwnerSectionProps> = ({
  formData,
  handleChange,
  mobilePhoneCountryCode = DEFAULT_COUNTRY_CODE,
  setMobilePhoneCountryCode = () => {},
  workPhoneCountryCode = DEFAULT_COUNTRY_CODE,
  setWorkPhoneCountryCode = () => {},
  travelPhoneCountryCode = DEFAULT_COUNTRY_CODE,
  setTravelPhoneCountryCode = () => {},
  hideTitle = false,
}) => {
  const [personalEmailError, setPersonalEmailError] = useState<string>("");
  const [workEmailError, setWorkEmailError] = useState<string>("");
  const [personalEmailTouched, setPersonalEmailTouched] = useState(false);
  const [workEmailTouched, setWorkEmailTouched] = useState(false);
  const [selectedMessagingApp, setSelectedMessagingApp] = useState<
    MessagingApp | ""
  >("");
  const [messagingHandlesByApp, setMessagingHandlesByApp] =
    useState<MessagingHandleMap>({});

  const handleFieldChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    handleChange(e);
  };

  const validateEmail = (email: string, isRequired: boolean): string => {
    if (!email && isRequired) {
      return "Email is required";
    }
    if (email && !EMAIL_REGEX.test(email)) {
      return "Invalid email format";
    }
    return "";
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    handleChange(e);

    if (name === "email" && personalEmailTouched) {
      setPersonalEmailError(validateEmail(value, true));
    } else if (name === "workEmail" && workEmailTouched) {
      setWorkEmailError(validateEmail(value, false));
    }
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "email") {
      setPersonalEmailTouched(true);
      setPersonalEmailError(validateEmail(value, true));
    } else if (name === "workEmail") {
      setWorkEmailTouched(true);
      setWorkEmailError(validateEmail(value, false));
    }
  };

  const syncMessagingFields = (
    nextSelectedApp: MessagingApp | "",
    nextHandlesByApp: MessagingHandleMap,
  ) => {
    const normalizedContacts = normalizeMessagingContacts(nextHandlesByApp);
    const selectedHandle = nextSelectedApp
      ? (nextHandlesByApp[nextSelectedApp] || "").trim()
      : "";

    const primaryContact: MessagingContact | null = nextSelectedApp
      ? {
          app: nextSelectedApp,
          handle: selectedHandle,
        }
      : (normalizedContacts[0] ?? null);

    handleFieldChange({
      target: {
        name: "messagingContacts",
        value: normalizedContacts,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>);

    handleFieldChange({
      target: {
        name: "messagingContact",
        value: primaryContact,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  useEffect(() => {
    const state = getMessagingState({
      messagingContact: formData.messagingContact,
      messagingContacts: formData.messagingContacts,
    });
    setSelectedMessagingApp(state.selectedApp);
    setMessagingHandlesByApp(state.handlesByApp);
  }, [formData.messagingContacts, formData.messagingContact]);

  const effectiveMobilePhoneCountryCode =
    formData.mobilePhoneCountryCode || mobilePhoneCountryCode;
  const effectiveWorkPhoneCountryCode =
    formData.workPhoneCountryCode || workPhoneCountryCode;
  const effectiveTravelPhoneCountryCode =
    formData.travelPhoneCountryCode || travelPhoneCountryCode;

  return (
    <div className="space-y-6">
      {!hideTitle && (
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          Owner Information
        </h2>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            First Name*
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName || ""}
            onChange={handleFieldChange}
            required
            className="w-full h-[41px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Last Name*
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName || ""}
            onChange={handleFieldChange}
            required
            className="w-full h-[41px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email*
          </label>
          <input
            type="text"
            name="email"
            value={formData.email || ""}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            required
            className={`w-full h-[41px] px-3 py-2 border ${personalEmailError && personalEmailTouched ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm`}
          />
          {personalEmailError && personalEmailTouched && (
            <p className="mt-1 text-sm text-red-600">{personalEmailError}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Work Email
          </label>
          <input
            type="text"
            name="workEmail"
            value={formData.workEmail || ""}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            className={`w-full h-[41px] px-3 py-2 border ${workEmailError && workEmailTouched ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm`}
          />
          {workEmailError && workEmailTouched && (
            <p className="mt-1 text-sm text-red-600">{workEmailError}</p>
          )}
        </div>
      </div>
      <div className="mt-4 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Mobile Phone*
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <CountryCodeSelect
                value={effectiveMobilePhoneCountryCode}
                onChange={(nextCode) => {
                  setMobilePhoneCountryCode(nextCode);
                  handleFieldChange({
                    target: {
                      name: "mobilePhoneCountryCode",
                      value: nextCode,
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                className="w-full sm:w-24"
                buttonClassName="h-[41px]"
              />
              <input
                type="tel"
                name="mobilePhone"
                value={formatPhoneBodyForDisplay(
                  stripCountryCodeForDisplay(
                    formData.mobilePhone,
                    effectiveMobilePhoneCountryCode,
                  ),
                )}
                onChange={(e) => {
                  handleFieldChange({
                    target: {
                      name: "mobilePhone",
                      value: normalizePhoneForStorage(
                        e.target.value,
                        effectiveMobilePhoneCountryCode,
                      ),
                    },
                  } as React.ChangeEvent<HTMLInputElement>);

                  handleFieldChange({
                    target: {
                      name: "mobilePhoneCountryCode",
                      value: effectiveMobilePhoneCountryCode,
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                minLength={8}
                maxLength={15}
                size={12}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Work Phone
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <CountryCodeSelect
                value={effectiveWorkPhoneCountryCode}
                onChange={(nextCode) => {
                  setWorkPhoneCountryCode(nextCode);
                  handleFieldChange({
                    target: {
                      name: "workPhoneCountryCode",
                      value: nextCode,
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                className="w-full sm:w-24"
                buttonClassName="h-[41px]"
              />
              <input
                type="tel"
                name="workPhone"
                value={formatPhoneBodyForDisplay(
                  stripCountryCodeForDisplay(
                    formData.workPhone,
                    effectiveWorkPhoneCountryCode,
                  ),
                )}
                onChange={(e) => {
                  handleFieldChange({
                    target: {
                      name: "workPhone",
                      value: normalizePhoneForStorage(
                        e.target.value,
                        effectiveWorkPhoneCountryCode,
                      ),
                    },
                  } as React.ChangeEvent<HTMLInputElement>);

                  handleFieldChange({
                    target: {
                      name: "workPhoneCountryCode",
                      value: effectiveWorkPhoneCountryCode,
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                minLength={8}
                maxLength={15}
                size={12}
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Travel Phone Number
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <CountryCodeSelect
                value={effectiveTravelPhoneCountryCode}
                onChange={(nextCode) => {
                  setTravelPhoneCountryCode(nextCode);
                  handleFieldChange({
                    target: {
                      name: "travelPhoneCountryCode",
                      value: nextCode,
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                className="w-full sm:w-24"
                buttonClassName="h-[41px]"
              />
              <input
                type="tel"
                name="travelPhone"
                value={formatPhoneBodyForDisplay(
                  stripCountryCodeForDisplay(
                    formData.travelPhone,
                    effectiveTravelPhoneCountryCode,
                  ),
                )}
                onChange={(e) => {
                  handleFieldChange({
                    target: {
                      name: "travelPhone",
                      value: normalizePhoneForStorage(
                        e.target.value,
                        effectiveTravelPhoneCountryCode,
                      ),
                    },
                  } as React.ChangeEvent<HTMLInputElement>);

                  handleFieldChange({
                    target: {
                      name: "travelPhoneCountryCode",
                      value: effectiveTravelPhoneCountryCode,
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                minLength={8}
                maxLength={15}
                size={12}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Messaging App
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <select
                value={selectedMessagingApp}
                onChange={(e) => {
                  const nextApp = e.target.value as MessagingApp | "";
                  setSelectedMessagingApp(nextApp);
                  syncMessagingFields(nextApp, messagingHandlesByApp);
                }}
                className="w-full sm:w-[calc(7.65rem+0.5ch)] sm:shrink-0 px-3 pr-9 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              >
                <option value="">Select</option>
                {MESSAGING_APPS.map((app) => (
                  <option key={app} value={app} className="text-sm">
                    {app}
                  </option>
                ))}
              </select>
              {selectedMessagingApp ? (
                <input
                  type="text"
                  value={getSelectedHandle(
                    selectedMessagingApp,
                    messagingHandlesByApp,
                  )}
                  onChange={(e) => {
                    const nextHandlesByApp = {
                      ...messagingHandlesByApp,
                      [selectedMessagingApp]: e.target.value,
                    };
                    setMessagingHandlesByApp(nextHandlesByApp);
                    syncMessagingFields(selectedMessagingApp, nextHandlesByApp);
                  }}
                  placeholder={`Enter ${selectedMessagingApp} handle`}
                  minLength={1}
                  maxLength={50}
                  size={13}
                  className="w-full sm:flex-1 sm:min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                />
              ) : (
                <div className="w-full sm:flex-1 sm:min-w-0 px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-400 text-sm">
                  Select app first
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerSection;
